import React, { useRef, Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Check, Star, Sun, Moon, Heart, Sparkles, Activity, Shield, Clock, Video, Layers, Package, User, Eye, FileText, HelpCircle, ChevronRight, MessageCircle, Zap } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { Accordion } from '../components/ui/Accordion';
import { useCart } from '../contexts/CartContext';
interface PricingPageProps {
  onNavigate: (page: string) => void;
}
const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.08
    }
  }
};
const staggerItem = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};
function AnimatedSection({
  children,
  className = ''
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <motion.section className={className} initial="hidden" whileInView="visible" viewport={{
    once: true,
    margin: '-60px'
  }} variants={{
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  }} data-id="element-24989">
      {children}
    </motion.section>;
}
export function PricingPage({
  onNavigate
}: PricingPageProps) {
  const servicesRef = useRef<HTMLDivElement>(null);
  const {
    addItem
  } = useCart();
  const handleBuyPackage = (pkg: {
    title: string;
    desc: string;
    price: number;
    oldPrice?: number;
  }) => {
    addItem({
      id: `pkg-${Date.now()}`,
      name: pkg.title,
      description: pkg.desc,
      price: pkg.price,
      type: 'package',
      format: 'PDF-отчёты',
      duration: 'Бессрочно',
      oldPrice: pkg.oldPrice,
      badges: [{
        label: 'Пакет отчётов',
        variant: 'success'
      }]
    });
    onNavigate('checkout');
  };
  return <div className="w-full bg-slate-50 min-h-screen" data-id="element-24990">
      {/* 1. HERO */}
      <div className="relative bg-slate-900 text-white overflow-hidden pt-6 pb-14 lg:pb-16" data-id="element-24991">
        <div className="absolute top-0 right-0 -translate-y-1/4 translate-x-1/4 w-[800px] h-[800px] bg-indigo-500/20 rounded-full blur-[120px] pointer-events-none" data-id="element-24992" />
        <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/4 w-[600px] h-[600px] bg-violet-500/15 rounded-full blur-[100px] pointer-events-none" data-id="element-24993" />
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.8)_1px,transparent_1px)] bg-[length:40px_40px] pointer-events-none" data-id="element-24994" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-24995">
          <Breadcrumbs items={[{
          label: 'Главная',
          onClick: () => onNavigate('home')
        }, {
          label: 'Цены'
        }]} className="mb-8 text-slate-400 [&_span]:text-slate-300 [&_button]:text-slate-400 [&_button:hover]:text-white [&_svg]:text-slate-500" data-id="element-24996" />

          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="max-w-3xl mx-auto text-center relative" data-id="element-24997">
            <motion.h1 variants={staggerItem} className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 leading-tight tracking-tight" data-id="element-24998">
              Выберите астрологический анализ
            </motion.h1>
            <motion.p variants={staggerItem} className="text-base sm:text-lg text-slate-300 mb-3 leading-relaxed max-w-2xl mx-auto" data-id="element-24999">
              Получите персональный астрологический разбор личности, отношений и
              жизненных периодов.
            </motion.p>
            <motion.p variants={staggerItem} className="text-sm text-slate-400 mb-7 max-w-xl mx-auto bg-white/5 py-2 px-4 rounded-full inline-block backdrop-blur-sm border border-white/10" data-id="element-25000">
              Каждая услуга включает бесплатный Beta-результат и полный
              Premium-отчёт.
            </motion.p>
            <motion.div variants={staggerItem} className="flex flex-col items-center" data-id="element-25001">
              <Button size="lg" onClick={() => onNavigate('free-chart')} className="bg-indigo-500 hover:bg-indigo-600 shadow-lg shadow-indigo-500/30 w-full sm:w-auto" data-id="element-25002">
                Построить натальную карту
                <ArrowRight className="ml-2 w-5 h-5" data-id="element-25003" />
              </Button>
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mt-6 text-xs sm:text-sm text-slate-400" data-id="element-25004">
                <div className="flex items-center" data-id="element-25005">
                  <User className="w-3.5 h-3.5 mr-1.5 text-indigo-400" data-id="element-25006" />
                  <span data-id="element-25007">10 000+ отчётов</span>
                </div>
                <div className="flex items-center" data-id="element-25008">
                  <Zap className="w-3.5 h-3.5 mr-1.5 text-amber-400" data-id="element-25009" />
                  <span data-id="element-25010">Мгновенный результат</span>
                </div>
                <div className="flex items-center" data-id="element-25011">
                  <Shield className="w-3.5 h-3.5 mr-1.5 text-emerald-400" data-id="element-25012" />
                  <span data-id="element-25013">Безопасная оплата</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* 2. КАК РАБОТАЕТ ПЛАТФОРМА */}
      <AnimatedSection className="py-12 bg-white" data-id="element-25014">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25015">
          <div className="text-center mb-10" data-id="element-25016">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25017">
              Как работает платформа
            </h2>
            <p className="text-sm text-slate-600" data-id="element-25018">
              Простой путь от ввода данных до получения полного персонального
              разбора
            </p>
          </div>

          <div className="relative" data-id="element-25019">
            <div className="hidden md:block absolute top-10 left-[15%] right-[15%] h-0.5 border-t-2 border-dashed border-slate-200" data-id="element-25020" />
            <div className="md:hidden absolute top-10 bottom-10 left-[1.75rem] w-0.5 border-l-2 border-dashed border-slate-200" data-id="element-25021" />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6" data-id="element-25022">
              {[{
              icon: <User className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-25023" />,
              num: 1,
              title: 'Введите данные рождения',
              desc: 'Укажите дату, время и место рождения для точного расчёта.'
            }, {
              icon: <Eye className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-25024" />,
              num: 2,
              title: 'Получите бесплатный Beta-результат',
              desc: 'Мгновенный краткий анализ основных показателей.'
            }, {
              icon: <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-indigo-500" data-id="element-25025" />,
              num: 3,
              title: 'Разблокируйте полный Premium-отчёт',
              desc: 'Подробный персональный разбор с рекомендациями.'
            }].map(step => <div key={step.num} className="relative flex md:flex-col items-start md:items-center" data-id="element-25026">
                  <div className="w-14 h-14 md:w-20 md:h-20 bg-white rounded-2xl md:rounded-full flex items-center justify-center mb-0 md:mb-4 border border-slate-100 shadow-sm relative z-10 flex-shrink-0 mr-4 md:mr-0" data-id="element-25027">
                    <div className="absolute -top-1.5 -right-1.5 md:top-0 md:right-0 w-5 h-5 md:w-6 md:h-6 bg-indigo-600 text-white text-[10px] md:text-xs font-bold rounded-full flex items-center justify-center md:translate-x-1/4 md:-translate-y-1/4 shadow-sm" data-id="element-25028">
                      {step.num}
                    </div>
                    {step.icon}
                  </div>
                  <div className="md:text-center pt-1 md:pt-0" data-id="element-25029">
                    <h3 className="text-sm md:text-base font-bold text-slate-900 mb-1" data-id="element-25030">
                      {step.title}
                    </h3>
                    <p className="text-xs md:text-sm text-slate-500" data-id="element-25031">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* 3. РАЗОВЫЕ УСЛУГИ */}
      <div ref={servicesRef} className="scroll-mt-4" data-id="element-25032">
        <AnimatedSection className="py-12 bg-slate-50" data-id="element-25033">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25034">
            <div className="text-center mb-10" data-id="element-25035">
              <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25036">
                Разовые астрологические услуги
              </h2>
              <p className="text-sm md:text-base text-slate-600 max-w-2xl mx-auto" data-id="element-25037">
                Выберите конкретный астрологический анализ и получите
                персональный отчёт.
              </p>
            </div>

            <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-5" variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} data-id="element-25038">
              {/* Натальная карта */}
              <motion.div variants={staggerItem} data-id="element-25039">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-indigo-200 transition-colors" data-id="element-25040">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-500" data-id="element-25041" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25042">
                    <div className="w-10 h-10 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center" data-id="element-25043">
                      <Moon className="w-5 h-5" data-id="element-25044" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25045">
                      Натальная карта
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25046">
                    Анализ личности, характера, талантов и жизненного
                    потенциала.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25047">
                    <div className="text-xs text-slate-500" data-id="element-25048">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25049">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25050">
                    <div className="flex items-center justify-between mb-2" data-id="element-25051">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25052">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25053">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25054">
                      {['Солнце', 'Луна', 'Асцендент', 'краткий психологический профиль'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25055">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25056" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25057" />
                  <div className="mb-5 flex-grow" data-id="element-25058">
                    <div className="flex items-center justify-between mb-2" data-id="element-25059">
                      <span className="text-xs font-bold text-indigo-600 uppercase tracking-wider" data-id="element-25060">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded" data-id="element-25061">
                        от 20 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25062">
                      {['полный анализ натальной карты', 'планеты в знаках', 'планеты в домах', 'аспекты', 'таланты и предназначение', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25063">
                          <Check className="w-3.5 h-3.5 text-indigo-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25064" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button fullWidth onClick={() => onNavigate('service-natal')} data-id="element-25065">
                    Построить карту
                  </Button>
                </Card>
              </motion.div>

              {/* Соляр */}
              <motion.div variants={staggerItem} data-id="element-25066">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-amber-200 transition-colors" data-id="element-25067">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-amber-500" data-id="element-25068" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25069">
                    <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center" data-id="element-25070">
                      <Sun className="w-5 h-5" data-id="element-25071" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25072">Соляр</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25073">
                    Астрологический прогноз на личный год.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25074">
                    <div className="text-xs text-slate-500" data-id="element-25075">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25076">
                      4 400 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25077">
                    <div className="flex items-center justify-between mb-2" data-id="element-25078">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25079">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25080">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25081">
                      {['главная тема года', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25082">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25083" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25084" />
                  <div className="mb-5 flex-grow" data-id="element-25085">
                    <div className="flex items-center justify-between mb-2" data-id="element-25086">
                      <span className="text-xs font-bold text-amber-600 uppercase tracking-wider" data-id="element-25087">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded" data-id="element-25088">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25089">
                      {['полный солярный анализ', 'ключевые сферы года', 'важные периоды', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25090">
                          <Check className="w-3.5 h-3.5 text-amber-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25091" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-solar')} data-id="element-25092">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Комбо */}
              <motion.div variants={staggerItem} data-id="element-25093">
                <Card padding="md" className="h-full flex flex-col border-violet-300 ring-2 ring-violet-100 shadow-lg shadow-violet-500/10 relative overflow-hidden group" data-id="element-25094">
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-violet-500 to-fuchsia-500" data-id="element-25095" />
                  <div className="absolute top-3 right-3 z-10" data-id="element-25096">
                    <Badge variant="pro" className="bg-violet-100 text-violet-700 border border-violet-200 text-[10px]" data-id="element-25097">
                      Самый популярный
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 mb-3 mt-1" data-id="element-25098">
                    <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-600 flex items-center justify-center" data-id="element-25099">
                      <Layers className="w-5 h-5" data-id="element-25100" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 pr-20" data-id="element-25101">
                      Комбо: Натал + Соляр
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25102">
                    Полный анализ личности и прогноз на ближайший год.
                  </p>
                  <div className="bg-violet-50 rounded-lg p-3 mb-4 border border-violet-100" data-id="element-25103">
                    <div className="text-xs text-violet-600 font-medium" data-id="element-25104">
                      Premium отчёт
                    </div>
                    <div className="flex items-baseline gap-2" data-id="element-25105">
                      <span className="text-2xl font-bold text-slate-900" data-id="element-25106">
                        6 900 ₽
                      </span>
                      <span className="text-sm text-slate-400 line-through" data-id="element-25107">
                        8 300 ₽
                      </span>
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25108">
                    <div className="flex items-center justify-between mb-2" data-id="element-25109">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25110">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25111">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25112">
                      {['краткий профиль личности', 'краткий прогноз года'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25113">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25114" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25115" />
                  <div className="mb-5 flex-grow" data-id="element-25116">
                    <div className="flex items-center justify-between mb-2" data-id="element-25117">
                      <span className="text-xs font-bold text-violet-600 uppercase tracking-wider" data-id="element-25118">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-violet-600 bg-violet-100 px-1.5 py-0.5 rounded" data-id="element-25119">
                        от 35 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25120">
                      {['полный анализ натальной карты', 'полный соляр', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25121">
                          <Check className="w-3.5 h-3.5 text-violet-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25122" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="primary" fullWidth onClick={() => onNavigate('service-combo')} className="bg-violet-600 hover:bg-violet-700" data-id="element-25123">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Совместимость */}
              <motion.div variants={staggerItem} data-id="element-25124">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-rose-200 transition-colors" data-id="element-25125">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-rose-400" data-id="element-25126" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25127">
                    <div className="w-10 h-10 rounded-lg bg-rose-50 text-rose-500 flex items-center justify-center" data-id="element-25128">
                      <Heart className="w-5 h-5" data-id="element-25129" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25130">
                      Совместимость (Синастрия)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25131">
                    Анализ совместимости двух людей.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25132">
                    <div className="text-xs text-slate-500" data-id="element-25133">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25134">
                      4 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25135">
                    <div className="flex items-center justify-between mb-2" data-id="element-25136">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25137">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25138">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25139">
                      {['общий уровень совместимости', 'краткий анализ'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25140">
                            <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25141" />
                            {t}
                          </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25142" />
                  <div className="mb-5 flex-grow" data-id="element-25143">
                    <div className="flex items-center justify-between mb-2" data-id="element-25144">
                      <span className="text-xs font-bold text-rose-500 uppercase tracking-wider" data-id="element-25145">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-rose-600 bg-rose-50 px-1.5 py-0.5 rounded" data-id="element-25146">
                        от 25 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25147">
                      {['эмоциональная совместимость', 'романтическая динамика', 'конфликтные зоны', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25148">
                          <Check className="w-3.5 h-3.5 text-rose-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25149" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-compatibility')} data-id="element-25150">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Транзиты */}
              <motion.div variants={staggerItem} data-id="element-25151">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-teal-200 transition-colors" data-id="element-25152">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-teal-500" data-id="element-25153" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25154">
                    <div className="w-10 h-10 rounded-lg bg-teal-50 text-teal-600 flex items-center justify-center" data-id="element-25155">
                      <Activity className="w-5 h-5" data-id="element-25156" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25157">
                      Прогноз (Транзиты)
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25158">
                    Астрологический прогноз на ближайшие месяцы.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25159">
                    <div className="text-xs text-slate-500" data-id="element-25160">Premium отчёт</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25161">
                      3 900 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25162">
                    <div className="flex items-center justify-between mb-2" data-id="element-25163">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25164">
                        Beta включает:
                      </span>
                      <Badge variant="neutral" size="sm" data-id="element-25165">
                        Бесплатно
                      </Badge>
                    </div>
                    <ul className="space-y-1" data-id="element-25166">
                      {['основной транзит', 'краткий прогноз'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-600" data-id="element-25167">
                          <Check className="w-3.5 h-3.5 text-slate-400 mr-2 mt-0.5 flex-shrink-0" data-id="element-25168" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25169" />
                  <div className="mb-5 flex-grow" data-id="element-25170">
                    <div className="flex items-center justify-between mb-2" data-id="element-25171">
                      <span className="text-xs font-bold text-teal-600 uppercase tracking-wider" data-id="element-25172">
                        Premium включает:
                      </span>
                      <span className="text-[10px] font-medium text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded" data-id="element-25173">
                        от 15 стр.
                      </span>
                    </div>
                    <ul className="space-y-1" data-id="element-25174">
                      {['полный анализ транзитов', 'ключевые даты', 'рекомендации'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25175">
                          <Check className="w-3.5 h-3.5 text-teal-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25176" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-forecast')} data-id="element-25177">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>

              {/* Консультация */}
              <motion.div variants={staggerItem} data-id="element-25178">
                <Card padding="md" className="h-full flex flex-col border-slate-200 relative overflow-hidden group hover:border-sky-200 transition-colors" data-id="element-25179">
                  <div className="absolute top-0 left-0 right-0 h-1 bg-sky-500" data-id="element-25180" />
                  <div className="flex items-center gap-3 mb-3" data-id="element-25181">
                    <div className="w-10 h-10 rounded-lg bg-sky-50 text-sky-600 flex items-center justify-center" data-id="element-25182">
                      <Video className="w-5 h-5" data-id="element-25183" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900" data-id="element-25184">
                      Консультация с астрологом
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-4" data-id="element-25185">
                    Персональная консультация с профессиональным астрологом.
                  </p>
                  <div className="bg-slate-50 rounded-lg p-3 mb-4 border border-slate-100" data-id="element-25186">
                    <div className="text-xs text-slate-500" data-id="element-25187">Личная встреча</div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-25188">
                      от 2 990 ₽
                    </div>
                  </div>
                  <div className="mb-3" data-id="element-25189">
                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-25190">
                      Beta:
                    </span>
                    <p className="text-xs text-slate-400 italic mt-1" data-id="element-25191">
                      Для консультаций Beta отсутствует
                    </p>
                  </div>
                  <hr className="border-slate-100 mb-3" data-id="element-25192" />
                  <div className="mb-5 flex-grow" data-id="element-25193">
                    <span className="text-xs font-bold text-sky-600 uppercase tracking-wider" data-id="element-25194">
                      Формат:
                    </span>
                    <ul className="space-y-1 mt-2" data-id="element-25195">
                      {['онлайн-встреча по видеосвязи', 'персональный разбор карты', 'ответы на ваши вопросы'].map((t, i) => <li key={i} className="flex items-start text-xs text-slate-700 font-medium" data-id="element-25196">
                          <Check className="w-3.5 h-3.5 text-sky-500 mr-2 mt-0.5 flex-shrink-0" data-id="element-25197" />
                          {t}
                        </li>)}
                    </ul>
                  </div>
                  <Button variant="outline" fullWidth onClick={() => onNavigate('service-consultation')} data-id="element-25198">
                    Подробнее
                  </Button>
                </Card>
              </motion.div>
            </motion.div>
          </div>
        </AnimatedSection>
      </div>

      {/* 5. TRUST / GUARANTEE */}
      <section className="py-8 bg-white border-y border-slate-100" data-id="element-25199">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6" data-id="element-25201">
            {[{
            icon: <Clock className="w-5 h-5 text-indigo-600" data-id="element-25202" />,
            bg: 'bg-indigo-50',
            title: 'Мгновенный результат',
            desc: 'Отчёт готов сразу после оплаты'
          }, {
            icon: <Shield className="w-5 h-5 text-emerald-600" data-id="element-25203" />,
            bg: 'bg-emerald-50',
            title: 'Безопасная оплата',
            desc: 'Надежное шифрование данных'
          }, {
            icon: <FileText className="w-5 h-5 text-amber-600" data-id="element-25204" />,
            bg: 'bg-amber-50',
            title: 'PDF-отчёт навсегда',
            desc: 'Доступен в личном кабинете'
          }, {
            icon: <MessageCircle className="w-5 h-5 text-sky-600" data-id="element-25205" />,
            bg: 'bg-sky-50',
            title: 'Поддержка 24/7',
            desc: 'Поможем с любым вопросом'
          }].map((item, i) => <div key={i} className="flex flex-col items-center text-center" data-id="element-25206">
                <div className={`w-10 h-10 rounded-full ${item.bg} flex items-center justify-center mb-2`} data-id="element-25207">
                  {item.icon}
                </div>
                <h4 className="text-xs font-bold text-slate-900 mb-0.5" data-id="element-25208">
                  {item.title}
                </h4>
                <p className="text-[10px] text-slate-500 leading-tight" data-id="element-25209">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. НЕ ЗНАЕТЕ, ЧТО ВЫБРАТЬ? */}
      <AnimatedSection className="py-12 bg-slate-50" data-id="element-25210">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25211">
          <div className="text-center mb-8" data-id="element-25212">
            <div className="w-11 h-11 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center mx-auto mb-3" data-id="element-25213">
              <HelpCircle className="w-5 h-5" data-id="element-25214" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25215">
              Не знаете, что выбрать?
            </h2>
            <p className="text-sm text-slate-600" data-id="element-25216">
              Выберите вашу главную цель, и мы подскажем подходящую услугу
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-25217">
            {[{
            goal: 'Хочу понять себя',
            service: 'Натальная карта',
            page: 'service-natal',
            icon: <Moon className="w-4 h-4" data-id="element-25218" />,
            color: 'text-indigo-600',
            borderHover: 'hover:border-indigo-300'
          }, {
            goal: 'Хочу прогноз на год',
            service: 'Соляр',
            page: 'service-solar',
            icon: <Sun className="w-4 h-4" data-id="element-25219" />,
            color: 'text-amber-600',
            borderHover: 'hover:border-amber-300'
          }, {
            goal: 'Хочу личность и прогноз',
            service: 'Комбо: Натал + Соляр',
            page: 'service-combo',
            icon: <Layers className="w-4 h-4" data-id="element-25220" />,
            color: 'text-violet-600',
            borderHover: 'hover:border-violet-300'
          }, {
            goal: 'Хочу понять отношения',
            service: 'Совместимость',
            page: 'service-compatibility',
            icon: <Heart className="w-4 h-4" data-id="element-25221" />,
            color: 'text-rose-600',
            borderHover: 'hover:border-rose-300'
          }, {
            goal: 'Хочу прогноз на ближайшее время',
            service: 'Транзиты',
            page: 'service-forecast',
            icon: <Activity className="w-4 h-4" data-id="element-25222" />,
            color: 'text-teal-600',
            borderHover: 'hover:border-teal-300'
          }, {
            goal: 'Хочу личный разбор',
            service: 'Консультация',
            page: 'service-consultation',
            icon: <Video className="w-4 h-4" data-id="element-25223" />,
            color: 'text-sky-600',
            borderHover: 'hover:border-sky-300'
          }].map((item, i) => <button key={i} onClick={() => onNavigate(item.page)} className={`flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-slate-200 text-left transition-all group hover:shadow-sm ${item.borderHover}`} data-id="element-25224">
                <div className={`w-9 h-9 rounded-lg bg-slate-50 ${item.color} flex items-center justify-center flex-shrink-0`} data-id="element-25225">
                  {item.icon}
                </div>
                <div className="flex-grow min-w-0" data-id="element-25226">
                  <div className="text-sm font-bold text-slate-900" data-id="element-25227">
                    {item.goal}
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-25228">→ {item.service}</div>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0 group-hover:text-slate-600 transition-colors" data-id="element-25229" />
              </button>)}
          </div>
        </div>
      </AnimatedSection>

      {/* 7. FAQ */}
      <AnimatedSection className="py-14 bg-white" data-id="element-25230">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-25231">
          <div className="text-center mb-8" data-id="element-25232">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" data-id="element-25233">
              Часто задаваемые вопросы
            </h2>
          </div>
          <Accordion items={[{
          id: '1',
          title: 'Что такое Beta-результат?',
          content: 'Beta — это бесплатный предварительный анализ, который вы получаете сразу после ввода данных рождения. Он показывает основные астрологические показатели и позволяет познакомиться с форматом анализа перед покупкой полного отчёта.'
        }, {
          id: '2',
          title: 'Чем Premium отличается от Beta?',
          content: 'Premium — это полный подробный отчёт с разбором всех элементов карты, прогнозами и практическими рекомендациями. Объём Premium-отчёта значительно больше и включает глубокий персональный анализ.'
        }, {
          id: '3',
          title: 'Можно ли использовать пакет для разных услуг?',
          content: 'Да! Каждый отчёт из пакета можно использовать для любой Premium-услуги: натальная карта, соляр, совместимость или прогноз (транзиты). Вы сами выбираете, на что потратить каждый отчёт.'
        }, {
          id: '4',
          title: 'В каком формате приходит отчёт?',
          content: 'Все отчёты предоставляются в формате PDF. Вы можете скачать их на странице результата сразу после оплаты, а также получить на email. Отчёты доступны в вашем личном кабинете бессрочно.'
        }, {
          id: '5',
          title: 'Сколько времени занимает расчёт?',
          content: 'Автоматические отчёты генерируются мгновенно — сразу после оплаты. Консультация с астрологом назначается на удобное для вас время.'
        }]} data-id="element-25234" />
        </div>
      </AnimatedSection>

      {/* 8. ФИНАЛЬНЫЙ CTA */}
      <AnimatedSection className="py-16 bg-slate-900 relative overflow-hidden" data-id="element-25235">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f46e520,#8b5cf620)]" data-id="element-25236" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-25237">
          <motion.div animate={{
          scale: [1, 1.1, 1]
        }} transition={{
          duration: 3,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="inline-block mb-5" data-id="element-25238">
            <Star className="w-8 h-8 text-indigo-400" data-id="element-25239" />
          </motion.div>
          <h2 className="text-2xl md:text-4xl font-bold text-white mb-4" data-id="element-25240">
            Начните исследовать свою натальную карту
          </h2>
          <p className="text-base text-indigo-100/80 mb-8 max-w-xl mx-auto" data-id="element-25241">
            Введите данные рождения и получите персональный астрологический
            анализ.
          </p>
          <Button size="lg" variant="outline" onClick={() => onNavigate('free-chart')} className="shadow-xl shadow-indigo-500/25 !bg-white !text-indigo-600 !border-white/30 hover:!bg-indigo-50" data-id="element-25242">
            Построить карту
            <ArrowRight className="ml-2 w-5 h-5" data-id="element-25243" />
          </Button>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm text-indigo-200/60" data-id="element-25244">
            <span className="flex items-center" data-id="element-25245">
              <Shield className="w-4 h-4 mr-1.5 text-emerald-400/80" data-id="element-25246" />{' '}
              Безопасная оплата
            </span>
            <span className="flex items-center" data-id="element-25247">
              <Clock className="w-4 h-4 mr-1.5 text-indigo-300/80" data-id="element-25248" /> Мгновенный
              результат
            </span>
          </div>
        </div>
      </AnimatedSection>
    </div>;
}